import { observer } from 'mobx-react';
import { ExportType, useExportStore } from '../../store/ExportStore';
import { Flex, MultiSelect, Select, Space, Text, Tooltip } from '@mantine/core';
import { useCohortStore } from '../../store';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { BaseExportButton } from './BaseExportButton';
import { useTranslation } from 'react-i18next';
import { BaseModal, MyIcon } from '@web/common-ui';
import { MAX_HORIZON, MAX_WAVE, staffHorizon } from '@web/common';
import { CohortType } from '@prisma/client';

// The label is the staff horizon (see staffHorizon); baseline keeps value "0".
// A REOCCURING round is never wave 0, so the wave list starts at 1. Baseline has
// no wave at all.
const BASELINE_HORIZON_VALUE = '0';
const horizonOptions = [
  { label: `${staffHorizon(0)}`, value: BASELINE_HORIZON_VALUE },
  ...Array.from({ length: MAX_HORIZON }, (_, i) => ({
    label: `${staffHorizon(i + 1)}`,
    value: `${i + 1}`,
  })),
];
const waveOptions = Array.from({ length: MAX_WAVE }, (_, i) => ({
  label: `${i + 1}`,
  value: `${i + 1}`,
}));

// Internal shorthand used by staff (Slack, support requests), not the
// participant- or profile-facing CohortType labels used elsewhere.
const EXPORTABLE_COHORT_TYPES: CohortType[] = [
  CohortType.ACCESS,
  CohortType.CONVENIENT_ACCESS,
  CohortType.CONTROL_GROUP,
];

function isExportableCohortType(value: string): value is CohortType {
  return EXPORTABLE_COHORT_TYPES.some((type) => type === value);
}

export const ExportModal = observer(() => {
  const exportStore = useExportStore();
  const cohortStore = useCohortStore();
  const { t } = useTranslation('organizer-dashboard');

  const [phoneTrackingCohortId, setPhoneTrackingCohortId] = useState<
    string | null
  >(null);
  const [phoneTrackingHorizon, setPhoneTrackingHorizon] = useState<
    string | null
  >(null);
  const [phoneTrackingWave, setPhoneTrackingWave] = useState<string | null>(
    null,
  );

  const cohortData = cohortStore.cohorts.map((cohort) => ({
    label: `${cohort.id} - ${DateTime.fromJSDate(cohort.startDate)}`,
    value: cohort.id,
  }));

  const cohortTypeLabel: Record<CohortType, string> = {
    [CohortType.NONE]: '',
    [CohortType.ACCESS]: t('ExportModal.GroupA'),
    [CohortType.CONVENIENT_ACCESS]: t('ExportModal.GroupB'),
    [CohortType.CONTROL_GROUP]: t('ExportModal.GroupC'),
  };
  const cohortTypeData = EXPORTABLE_COHORT_TYPES.map((value) => ({
    label: cohortTypeLabel[value],
    value,
  }));

  const [participantCohortIds, setParticipantCohortIds] = useState<string[]>(
    [],
  );
  const [participantCohortTypes, setParticipantCohortTypes] = useState<
    CohortType[]
  >([]);

  useEffect(() => {
    cohortStore.loadCohorts();
  }, [cohortStore]);

  useEffect(() => {
    if (exportStore.downloadData) {
      exportData(
        exportStore.downloadData.data,
        exportStore.downloadData.filename,
      );
    }
  }, [exportStore.downloadData]);

  const onModalClose = () => {
    exportStore.resetModal();
    setPhoneTrackingCohortId(null);
    setPhoneTrackingHorizon(null);
    setPhoneTrackingWave(null);
    setParticipantCohortIds([]);
    setParticipantCohortTypes([]);
  };

  const exportData = (data: string, filename: string) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode!.removeChild(link);
  };

  const optionList = (
    <Flex direction="column" gap="10px">
      <Space h="10px" />
      <BaseExportButton
        label="Onboarding Daten"
        onClick={() => (exportStore.exportType = ExportType.Onboarding)}
      />
      <BaseExportButton
        label="Clusterin Daten eportieren"
        onClick={() => (exportStore.exportType = ExportType.Clustering)}
      />
      <BaseExportButton
        label="Alle Studiendaten exportieren"
        onClick={() => (exportStore.exportType = ExportType.AllStudyData)}
      />
      <BaseExportButton
        label={t('ExportModal.PhoneTracking')}
        onClick={() => (exportStore.exportType = ExportType.PhoneTracking)}
      />
      <BaseExportButton
        label={t('ExportModal.ParticipantsTitle')}
        onClick={() => (exportStore.exportType = ExportType.Participants)}
      />
    </Flex>
  );

  const downloadSettings = () => {
    switch (exportStore.exportType) {
      case ExportType.Onboarding: {
        return cohortSelection(exportStore.download);
      }
      case ExportType.Clustering: {
        return cohortSelection(exportStore.download);
      }
      case ExportType.AllStudyData: {
        return cohortSelection(exportStore.download);
      }
      case ExportType.PhoneTracking: {
        return phoneTrackingSelection();
      }
      case ExportType.Participants: {
        return participantsSelection();
      }
    }
  };

  const cohortSelection = (onDownload: () => void) => (
    <Flex direction="column" gap="40px">
      <Select
        placeholder={t('ExportModal.Select')}
        label={t('ExportModal.Cohort')}
        required
        w="350px"
        data={cohortData}
        onChange={(value) => {
          if (value) {
            exportStore.downloadSettings = {
              type: ExportType.Onboarding,
              cohortId: value,
            };
          }
        }}
      />

      <BaseExportButton label={t('ExportModal.Button')} onClick={onDownload} />
    </Flex>
  );

  const isBaselineSelected = phoneTrackingHorizon === BASELINE_HORIZON_VALUE;

  const phoneTrackingReady =
    phoneTrackingCohortId !== null &&
    phoneTrackingHorizon !== null &&
    (isBaselineSelected || phoneTrackingWave !== null);

  const phoneTrackingSelection = () => (
    <Flex direction="column" gap="40px">
      <Select
        placeholder={t('ExportModal.Select')}
        label={t('ExportModal.Cohort')}
        required
        w="350px"
        data={cohortData}
        value={phoneTrackingCohortId}
        onChange={setPhoneTrackingCohortId}
      />
      <Select
        placeholder={t('ExportModal.Select')}
        label={t('ExportModal.Horizon')}
        required
        w="350px"
        data={horizonOptions}
        value={phoneTrackingHorizon}
        onChange={setPhoneTrackingHorizon}
      />
      {!isBaselineSelected && (
        <Select
          placeholder={t('ExportModal.Select')}
          label={t('ExportModal.Wave')}
          required
          w="350px"
          data={waveOptions}
          value={phoneTrackingWave}
          onChange={setPhoneTrackingWave}
        />
      )}

      <BaseExportButton
        label={t('ExportModal.Button')}
        disabled={!phoneTrackingReady}
        onClick={() => {
          if (phoneTrackingCohortId === null || phoneTrackingHorizon === null) {
            return;
          }
          if (!isBaselineSelected && phoneTrackingWave === null) {
            return;
          }
          exportStore.downloadSettings = {
            type: ExportType.PhoneTracking,
            cohortId: phoneTrackingCohortId,
            horizon: Number(phoneTrackingHorizon),
            wave: isBaselineSelected ? 0 : Number(phoneTrackingWave),
          };
          exportStore.download();
        }}
      />
    </Flex>
  );

  const participantsSelection = () => (
    <Flex direction="column" gap="40px">
      <MultiSelect
        placeholder={t('ExportModal.AllHint')}
        label={t('ExportModal.Cohort')}
        w="350px"
        data={cohortData}
        value={participantCohortIds}
        onChange={setParticipantCohortIds}
      />
      <MultiSelect
        placeholder={t('ExportModal.AllHint')}
        label={
          <Flex align="center" gap="4px">
            <Text size="sm" fw={500}>
              {t('ExportModal.CohortTypes')}
            </Text>
            <Tooltip
              multiline
              maw={250}
              withArrow
              events={{ hover: true, focus: true, touch: true }}
              label={t('ExportModal.CohortTypesInfo')}
            >
              <MyIcon
                name="info"
                style={{ fill: '#3D7F53', height: '15px', width: '15px' }}
              />
            </Tooltip>
          </Flex>
        }
        w="350px"
        data={cohortTypeData}
        value={participantCohortTypes}
        onChange={(values) =>
          setParticipantCohortTypes(values.filter(isExportableCohortType))
        }
      />

      <BaseExportButton
        label={t('ExportModal.Button')}
        onClick={() => {
          exportStore.downloadSettings = {
            type: ExportType.Participants,
            cohortIds: participantCohortIds,
            cohortTypes: participantCohortTypes,
          };
          exportStore.download();
        }}
      />
    </Flex>
  );

  const isPhoneTracking = exportStore.exportType === ExportType.PhoneTracking;
  const isParticipants = exportStore.exportType === ExportType.Participants;

  const modalTitle = isPhoneTracking
    ? t('ExportModal.PhoneTracking')
    : isParticipants
      ? t('ExportModal.ParticipantsTitle')
      : t('ExportModal.Title');

  const modalDescription = isPhoneTracking
    ? t('ExportModal.PhoneTrackingDescription')
    : isParticipants
      ? t('ExportModal.ParticipantsDescription')
      : t('ExportModal.Description');

  return (
    <BaseModal
      size="500px"
      opened={exportStore.open}
      onClose={onModalClose}
      title={modalTitle}
      description={modalDescription}
      children={!exportStore.exportType ? optionList : downloadSettings()}
    ></BaseModal>
  );
});
